import { ApiError } from '../api/errors.js';

/**
 * Attaching server-side validation failures to the inputs that caused them.
 *
 * Pure and separately tested because the interesting case is the awkward one:
 * a server naming a field the form does not render. Setting an error on a
 * non-existent field leaves react-hook-form permanently invalid with nothing
 * on screen to explain it, and the form simply stops submitting.
 */

/** Minimal shape of react-hook-form's `setError`, so this needs no RHF import. */
export type SetFieldError = (field: string, error: { message: string }) => void;

/**
 * Applies an error's field messages.
 *
 * Returns whether it handled the error. `false` means the caller should
 * surface it some other way — a toast, usually — and `true` means the message
 * is already on screen beside the offending input, where a toast would repeat
 * it.
 *
 * `knownFields`, when given, is the allowlist of fields the form renders.
 */
export function applyServerErrors(
  error: unknown,
  setError: SetFieldError,
  knownFields?: readonly string[],
): boolean {
  if (!(error instanceof ApiError)) return false;

  const entries = Object.entries(error.fieldErrors).filter(
    ([field]) => knownFields === undefined || knownFields.includes(field),
  );

  if (entries.length === 0) return false;

  for (const [field, message] of entries) setError(field, { message });
  return true;
}
